/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { Form, DateInput, Tooltip } from '@mairistem/composants';
import { Section } from '@mairistem/administres-utils';

import { DATE_FORMAT } from '@/constants';
import { createTimeInputHandler } from '@/utils';

import { ChampLieu } from '@/components/Common';

interface InformationsEvenementDecesProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (
    event: React.SyntheticEvent | null,
    payload: { name: string; value: unknown },
  ) => void;
}

/**
 * Bloc « INFORMATIONS SUR L'ÉVÉNEMENT » de l'acte de décès : date et heure du
 * décès, lieu du décès.
 *
 * Construit avec les mêmes primitives qu'`InformationsEvenementReconnaissance`
 * (`Section`, `DateInput` + `Tooltip`, `createTimeInputHandler`, `ChampLieu`)
 * pour rester visuellement cohérent.
 *
 * ⚠️ Pas de dropdown « auteur » ici : c'est le propre de la reconnaissance, où
 * l'auteur commande la ventilation des colonnes date/lieu entre père et mère.
 * Un décès n'a qu'un seul jeu de date/heure.
 *
 * ⚠️ Le lieu saisi ici est stocké sur le DÉFUNT (`ai_dec_*`), pas sur l'acte :
 * `ad_dc_idlieu` n'est pas une clé de lieu. `buildActeDecesPayload` fait le
 * partage.
 */
const InformationsEvenementDeces = (props: InformationsEvenementDecesProps) => {
  const { data, errors = {}, onChange } = props;

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      onChange(null, {
        name: 'dateDeces',
        value: _.isNil(date) ? null : dayjs(date).format(DATE_FORMAT),
      });
    },
    [onChange],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(onChange),
    [onChange],
  );

  /**
   * Validité de la commune à la date du décès, et non à celle de l'acte : une
   * transcription ou un jugement déclaratif peut être dressé des années après,
   * après une fusion de communes.
   */
  const dateValiditeLieu = !_.isEmpty(data?.dateDeces)
    ? dayjs(data.dateDeces, DATE_FORMAT).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  return (
    <Section title="INFORMATIONS SUR L'ÉVÉNEMENT" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            gap: '1em',
          }}
        >
          <Form.Field required error={!_.isNil(errors?.dateDeces)}>
            <label>Décédé le</label>
            <Tooltip
              trigger={
                <DateInput
                  value={
                    data?.dateDeces
                      ? dayjs(data.dateDeces, DATE_FORMAT).toDate()
                      : undefined
                  }
                  onChange={handleDateChange}
                />
              }
              type={errors?.dateDeces ? 'error' : ''}
              message={errors?.dateDeces}
            />
          </Form.Field>
          <Form.Group style={{ alignItems: 'flex-end' }}>
            <Tooltip
              trigger={
                <Form.Input
                  label="à"
                  placeholder="hh"
                  name="heureDeces"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.heureDeces)}
                  value={
                    !_.isNil(data?.heureDeces)
                      ? String(data.heureDeces).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.heureDeces ? 'error' : ''}
              message={errors?.heureDeces}
            />
            <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>:</span>
            <Tooltip
              trigger={
                <Form.Input
                  placeholder="mm"
                  name="minuteDeces"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.minuteDeces)}
                  value={
                    !_.isNil(data?.minuteDeces)
                      ? String(data.minuteDeces).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.minuteDeces ? 'error' : ''}
              message={errors?.minuteDeces}
            />
          </Form.Group>
        </div>
        <ChampLieu
          data={data}
          errors={errors}
          onChange={onChange}
          name="adresseDeces"
          title="Lieu de décès"
          dateValidite={dateValiditeLieu}
        />
      </Form>
    </Section>
  );
};

export default InformationsEvenementDeces;
